import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Smile, Frown, Heart, AlertCircle } from 'lucide-react';
import { EmotionAnalysis } from '../../services/analyticsApi';

interface EmotionAnalysisCardProps {
  analysis: EmotionAnalysis;
}

export const EmotionAnalysisCard: React.FC<EmotionAnalysisCardProps> = ({ analysis }) => {
  const emotionColors: Record<string, string> = {
    joy: 'text-green-600',
    sadness: 'text-blue-600',
    anger: 'text-red-600',
    fear: 'text-purple-600',
    surprise: 'text-yellow-600',
    disgust: 'text-orange-600',
    neutral: 'text-gray-600'
  };

  const emotionEmojis: Record<string, string> = {
    joy: '😊',
    sadness: '😢',
    anger: '😠',
    fear: '😰',
    surprise: '😮',
    disgust: '🤢',
    neutral: '😐'
  };

  const getEmotionIcon = (emotion: string) => {
    switch (emotion.toLowerCase()) {
      case 'joy':
        return <Smile className="h-4 w-4" />;
      case 'sadness':
        return <Frown className="h-4 w-4" />;
      case 'anger':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Heart className="h-4 w-4" />;
    }
  };

  const sortedEmotions = Object.entries(analysis.emotion_distribution || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Heart className="h-5 w-5 text-pink-600" />
          Emotion Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Primary Emotion */}
        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{emotionEmojis[analysis.dominant_emotion] || '🤔'}</span>
            <div>
              <p className="text-sm text-gray-600">Primary Emotion</p>
              <p className="text-lg font-semibold capitalize">{analysis.dominant_emotion}</p>
            </div>
          </div>
          <Badge variant="secondary" className="text-sm">
            {((analysis.emotion_distribution?.[analysis.dominant_emotion] || 0) * 100).toFixed(0)}%
          </Badge>
        </div>

        {/* Emotion Distribution */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Emotion Distribution</h4>
          {sortedEmotions.map(([emotion, score]) => (
            <div key={emotion} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{emotionEmojis[emotion] || '🤔'}</span>
                  <span className={`capitalize ${emotionColors[emotion] || 'text-gray-600'}`}>
                    {emotion}
                  </span>
                </div>
                <span className="font-mono text-xs text-gray-500">
                  {(score * 100).toFixed(1)}%
                </span>
              </div>
              <Progress value={score * 100} className="h-2" />
            </div>
          ))}
        </div>

        {/* Emotional Metrics */}
        <div className="space-y-2 pt-3 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Emotional Metrics</h4>
          
          {/* Valence & Arousal */}
          <div className="grid grid-cols-2 gap-3">
            {analysis.average_valence !== undefined && analysis.average_valence !== null && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Valence (Positivity)</p>
                <p className="text-lg font-semibold text-blue-700">
                  {(analysis.average_valence * 100).toFixed(0)}%
                </p>
                <Progress value={analysis.average_valence * 100} className="h-1 mt-2" />
              </div>
            )}
            {analysis.average_arousal !== undefined && analysis.average_arousal !== null && (
              <div className="bg-orange-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Arousal (Energy)</p>
                <p className="text-lg font-semibold text-orange-700">
                  {(analysis.average_arousal * 100).toFixed(0)}%
                </p>
                <Progress value={analysis.average_arousal * 100} className="h-1 mt-2" />
              </div>
            )}
          </div>

          {/* Volatility */}
          {analysis.emotional_volatility !== undefined && analysis.emotional_volatility !== null && (
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">Emotional Volatility</p>
              <p className="text-lg font-semibold text-purple-700">
                {(analysis.emotional_volatility * 100).toFixed(0)}%
              </p>
              <Progress value={analysis.emotional_volatility * 100} className="h-1 mt-2" />
            </div>
          )}
        </div>

        {/* Analysis Quality */}
        {analysis.emotion_confidence !== undefined && analysis.emotion_confidence !== null && (
          <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span>Confidence Score</span>
              <span className="font-mono">{(analysis.emotion_confidence * 100).toFixed(1)}%</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

